import React from 'react';
import styled, { keyframes } from 'styled-components';

/** Visible in light & dark: mix muted with foreground so bars aren’t lost on pale backgrounds */
const skelSurface = (p: { theme: { foreground: string; muted: string } }) =>
  `color-mix(in oklch, ${p.theme.foreground} 14%, ${p.theme.muted})`;

const skelEdge = (p: { theme: { foreground: string; border: string } }) =>
  `color-mix(in oklch, ${p.theme.foreground} 10%, ${p.theme.border})`;

const pulse = keyframes`
  0%, 100% { opacity: 0.78; }
  50% { opacity: 1; }
`;

const Wrap = styled.div`
  padding: 24px;
  max-width: 1280px;
  margin: 0 auto;
  min-height: 50vh;
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 24px;
  @media (min-width: 1024px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const Block = styled.div<{ $h: number; $mb?: number; $w?: string }>`
  height: ${(p) => p.$h}px;
  width: ${(p) => p.$w ?? '100%'};
  border-radius: 10px;
  background: ${(p) => skelSurface(p)};
  border: 1px solid ${(p) => skelEdge(p)};
  margin-bottom: ${(p) => p.$mb ?? 12}px;
  animation: ${pulse} 1.15s ease-in-out infinite;
`;

const CardBlock = styled(Block)`
  border-radius: 10px;
  margin-bottom: 0;
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 1fr 0.8fr 0.8fr;
  gap: 12px;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid ${(p) => p.theme.border};
  &:last-child {
    border-bottom: none;
  }
`;

const TableHead = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 1fr 0.8fr 0.8fr;
  gap: 12px;
  padding-bottom: 10px;
  margin-bottom: 4px;
  border-bottom: 1px solid ${(p) => p.theme.border};
`;

const SubNavRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const Pill = styled.div`
  width: 88px;
  height: 32px;
  border-radius: 999px;
  background: ${(p) => skelSurface(p)};
  border: 1px solid ${(p) => skelEdge(p)};
  animation: ${pulse} 1.15s ease-in-out infinite;
`;

const TableShell = styled.div`
  border-radius: 12px;
  border: 1px solid ${(p) => p.theme.border};
  padding: 12px 16px;
`;

const GlobalPageHead = styled.div`
  margin-bottom: 20px;
`;

const GlobalToolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-bottom: 18px;
`;

const StatMini = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
`;

const StatMiniIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  flex-shrink: 0;
  background: ${(p) => skelSurface(p)};
  border: 1px solid ${(p) => skelEdge(p)};
  animation: ${pulse} 1.15s ease-in-out infinite;
`;

const StatMiniText = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 2px;
`;

/**
 * Layout-aware placeholder for **in-page** loading (lists, settings sections, etc.).
 * Do not use for global auth/permission gates or Suspense — use {@link RouteGateFallback} instead.
 */
const PageRouteSkeleton: React.FC = () => (
  <Wrap aria-busy="true" aria-label="Loading page">
    <GlobalPageHead>
      <Block $h={30} $w="min(52%, 280px)" $mb={10} />
      <Block $h={14} $w="min(78%, 420px)" $mb={0} />
    </GlobalPageHead>

    <StatsRow>
      {[1, 2, 3, 4].map((i) => (
        <CardBlock key={i} $h={110} $mb={0} style={{ padding: 12, boxSizing: 'border-box' as const }}>
          <StatMini>
            <StatMiniIcon />
            <StatMiniText>
              <Block $h={11} $w="52%" $mb={0} />
              <Block $h={22} $w="72%" $mb={0} />
              <Block $h={10} $w="88%" $mb={0} />
            </StatMiniText>
          </StatMini>
        </CardBlock>
      ))}
    </StatsRow>

    <GlobalToolbar>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', flex: '1 1 200px' }}>
        <Pill />
        <Pill />
        <Pill />
        <Pill />
      </div>
      <Block $h={36} style={{ flex: '0 1 220px', minWidth: 160, maxWidth: 260 }} $mb={0} />
      <Block $h={36} $w="96px" $mb={0} />
    </GlobalToolbar>

    <TableShell>
      <TableHead>
        <Block $h={14} $mb={0} />
        <Block $h={14} $mb={0} />
        <Block $h={14} $mb={0} />
        <Block $h={14} $mb={0} />
      </TableHead>
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <TableRow key={i}>
          <Block $h={16} $mb={0} />
          <Block $h={16} $mb={0} />
          <Block $h={16} $mb={0} />
          <Block $h={16} $mb={0} />
        </TableRow>
      ))}
    </TableShell>
  </Wrap>
);

/** Stats strip + main panel — generic list pages while React Query loads. */
export const ListPageSkeleton: React.FC = () => (
  <Wrap aria-busy="true" aria-label="Loading content">
    <Block $h={28} $w="38%" $mb={20} />
    <StatsRow>
      <Block $h={92} $mb={0} />
      <Block $h={92} $mb={0} />
      <Block $h={92} $mb={0} />
      <Block $h={92} $mb={0} />
    </StatsRow>
    <Block $h={400} />
  </Wrap>
);

/**
 * Payments dashboard: metrics row (like InfoCards) + wallet + table with header.
 */
/**
 * Tenant portal dashboard: welcome copy, 4 stat cards, payment card strip, segmented tabs + table.
 * Matches landlord page skeleton rhythm (PageRouteSkeleton / PaymentsPageSkeleton) with portal layout width.
 */
const TenantPortalSkeletonWrap = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
  min-height: 60vh;
`;

const TenantWelcomeSk = styled.div`
  margin-bottom: 28px;
`;

const TenantPaymentCardSk = styled.div`
  border-radius: 16px;
  border: 1px solid ${(p) => p.theme.border};
  background: ${(p) => p.theme.card};
  padding: 16px 20px;
  margin-bottom: 20px;
  display: grid;
  grid-template-columns: 1fr 1px 1.2fr 140px;
  gap: 16px;
  align-items: stretch;
  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const TenantPaymentSkDivider = styled.div`
  width: 1px;
  background: ${(p) => p.theme.border};
  @media (max-width: 720px) {
    display: none;
  }
`;

const TenantTabsHeaderSk = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

const TenantSegmentedSk = styled.div`
  display: inline-flex;
  gap: 4px;
  padding: 5px;
  border-radius: 14px;
  background: ${(p) => p.theme.muted};
  border: 1px solid ${(p) => p.theme.border};
`;

/** Financial records Ant table body only — matches column rhythm; use inside portal card or Suspense. */
const TenantFinTableShell = styled(TableShell)`
  overflow-x: auto;
`;

const TenantFinTableHead = styled.div`
  display: grid;
  grid-template-columns: 100px minmax(96px, 1.6fr) 88px 78px 72px 72px 68px 112px;
  gap: 10px;
  align-items: center;
  padding-bottom: 10px;
  margin-bottom: 4px;
  border-bottom: 1px solid ${(p) => p.theme.border};
  min-width: 720px;
`;

const TenantFinTableRow = styled.div`
  display: grid;
  grid-template-columns: 100px minmax(96px, 1.6fr) 88px 78px 72px 72px 68px 112px;
  gap: 10px;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid ${(p) => p.theme.border};
  min-width: 720px;
  &:last-child {
    border-bottom: none;
  }
`;

export const TenantFinancialTableSkeleton: React.FC = () => (
  <TenantFinTableShell aria-busy="true" aria-label="Loading financial records">
    <TenantFinTableHead>
      <Block $h={14} $mb={0} />
      <Block $h={14} $mb={0} />
      <Block $h={14} $mb={0} />
      <Block $h={14} $mb={0} />
      <Block $h={14} $mb={0} />
      <Block $h={14} $mb={0} />
      <Block $h={14} $mb={0} />
      <Block $h={14} $mb={0} />
    </TenantFinTableHead>
    {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
      <TenantFinTableRow key={i}>
        <Block $h={16} $mb={0} />
        <Block $h={16} $mb={0} />
        <Block $h={16} $mb={0} />
        <Block $h={16} $mb={0} />
        <Block $h={16} $mb={0} />
        <Block $h={16} $mb={0} />
        <Block $h={16} $mb={0} />
        <Block $h={16} $mb={0} />
      </TenantFinTableRow>
    ))}
  </TenantFinTableShell>
);

export const TenantPortalPageSkeleton: React.FC = () => (
  <TenantPortalSkeletonWrap aria-busy="true" aria-label="Loading tenant dashboard">
    <TenantWelcomeSk>
      <Block $h={36} $w="min(72%, 420px)" $mb={10} />
      <Block $h={16} $w="min(88%, 520px)" $mb={0} />
    </TenantWelcomeSk>

    <StatsRow>
      {[1, 2, 3, 4].map((i) => (
        <CardBlock key={i} $h={110} $mb={0} style={{ padding: 12, boxSizing: 'border-box' as const }}>
          <StatMini>
            <StatMiniIcon />
            <StatMiniText>
              <Block $h={11} $w="52%" $mb={0} />
              <Block $h={22} $w="72%" $mb={0} />
              <Block $h={10} $w="88%" $mb={0} />
            </StatMiniText>
          </StatMini>
        </CardBlock>
      ))}
    </StatsRow>

    <TenantPaymentCardSk>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0 }}>
        <Block $h={14} $w="40%" $mb={0} />
        <Block $h={32} $w="85%" $mb={0} />
        <Block $h={12} $w="55%" $mb={0} />
      </div>
      <TenantPaymentSkDivider />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0, justifyContent: 'center' }}>
        <Block $h={14} $w="70%" $mb={0} />
        <Block $h={14} $w="50%" $mb={0} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, justifyContent: 'center' }}>
        <Block $h={40} $mb={0} style={{ borderRadius: 10 }} />
        <Block $h={36} $mb={0} style={{ borderRadius: 10 }} />
      </div>
    </TenantPaymentCardSk>

    <TenantTabsHeaderSk>
      <div style={{ flex: '1 1 200px', minWidth: 0 }}>
        <Block $h={18} $w="min(220px, 55%)" $mb={8} />
        <Block $h={12} $w="min(340px, 92%)" $mb={0} />
      </div>
      <TenantSegmentedSk>
        <Block $h={38} $w="112px" $mb={0} style={{ borderRadius: 10 }} />
        <Block $h={38} $w="112px" $mb={0} style={{ borderRadius: 10 }} />
      </TenantSegmentedSk>
    </TenantTabsHeaderSk>

    <TableShell>
      <TableHead>
        <Block $h={14} $mb={0} />
        <Block $h={14} $mb={0} />
        <Block $h={14} $mb={0} />
        <Block $h={14} $mb={0} />
      </TableHead>
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <TableRow key={i}>
          <Block $h={16} $mb={0} />
          <Block $h={16} $mb={0} />
          <Block $h={16} $mb={0} />
          <Block $h={16} $mb={0} />
        </TableRow>
      ))}
    </TableShell>
  </TenantPortalSkeletonWrap>
);

export const PaymentsPageSkeleton: React.FC = () => (
  <Wrap aria-busy="true" aria-label="Loading payments">
    <Block $h={28} $w="42%" $mb={8} />
    <Block $h={16} $w="58%" $mb={22} />
    <StatsRow>
      <CardBlock $h={96} $mb={0} />
      <CardBlock $h={96} $mb={0} />
      <CardBlock $h={96} $mb={0} />
      <CardBlock $h={96} $mb={0} />
    </StatsRow>
    <PortfolioCard>
      <PortfolioHeaderBar>
        <Block $h={18} $w="34%" $mb={0} />
        <Block $h={32} $w="32px" $mb={0} style={{ borderRadius: 8 }} />
      </PortfolioHeaderBar>
      <PortfolioToolbar>
        <TabPillRow>
          <Pill />
          <Pill />
          <Pill />
        </TabPillRow>
        <Block $h={38} style={{ flex: '1 1 220px', minWidth: 180, maxWidth: 360 }} $mb={0} />
      </PortfolioToolbar>
      <div style={{ padding: '16px' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: 16,
            marginBottom: 16,
          }}
        >
          <CardBlock $h={128} $mb={0} />
          <CardBlock $h={128} $mb={0} />
        </div>
        <TableShell>
          <TableHead>
            <Block $h={14} $mb={0} />
            <Block $h={14} $mb={0} />
            <Block $h={14} $mb={0} />
            <Block $h={14} $mb={0} />
          </TableHead>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <TableRow key={i}>
              <Block $h={16} $mb={0} />
              <Block $h={16} $mb={0} />
              <Block $h={16} $mb={0} />
              <Block $h={16} $mb={0} />
            </TableRow>
          ))}
        </TableShell>
      </div>
    </PortfolioCard>
  </Wrap>
);

const PortfolioCard = styled.div`
  border-radius: 12px;
  border: 1px solid ${(p) => p.theme.border};
  background: ${(p) => p.theme.card};
  overflow: hidden;
  margin-bottom: 8px;
`;

const PortfolioToolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid ${(p) => p.theme.border};
  background: ${(p) => `color-mix(in srgb, ${p.theme.muted} 55%, ${p.theme.card})`};
`;

const PortfolioHeaderBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid ${(p) => p.theme.border};
`;

const TabPillRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

/** Property portfolio grid only (stats/header stay visible during refresh). */
const PortfolioGridInner: React.FC = () => (
  <div
    style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
      gap: 16,
    }}
  >
    {[1, 2, 3, 4, 5, 6].map((i) => (
      <CardBlock key={i} $h={188} $mb={0} />
    ))}
  </div>
);

export const PropertyPortfolioGridSkeleton: React.FC = () => (
  <div style={{ padding: '16px' }} aria-busy="true" aria-label="Loading properties">
    <PortfolioGridInner />
  </div>
);

/** Portfolio card body during refresh — toolbar + grid like PropertiesPageSkeleton. */
export const PropertyPortfolioSectionSkeleton: React.FC = () => (
  <div aria-busy="true" aria-label="Loading properties">
    <PortfolioToolbar>
      <TabPillRow>
        <Pill />
        <Pill />
        <Pill />
        <Pill />
      </TabPillRow>
      <Block $h={38} style={{ flex: '1 1 220px', minWidth: 180, maxWidth: 360 }} $mb={0} />
    </PortfolioToolbar>
    <div style={{ padding: '16px' }}>
      <PortfolioGridInner />
    </div>
  </div>
);

const TableSectionBody: React.FC<{ rows: number; columns: number }> = ({ rows, columns }) => (
  <div style={{ padding: 16 }}>
    <TableShell>
      <TableHead style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
        {Array.from({ length: columns }).map((_, i) => (
          <Block key={i} $h={14} $mb={0} />
        ))}
      </TableHead>
      {Array.from({ length: rows }).map((_, i) => (
        <TableRow key={i} style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
          {Array.from({ length: columns }).map((_, j) => (
            <Block key={j} $h={16} $mb={0} />
          ))}
        </TableRow>
      ))}
    </TableShell>
  </div>
);

/** Generic data-table body skeleton for in-section refresh states. */
export const DataTableSectionSkeleton: React.FC<{ rows?: number; columns?: number }> = ({
  rows = 6,
  columns = 4,
}) => (
  <div aria-busy="true" aria-label="Loading table">
    <TableSectionBody rows={rows} columns={columns} />
  </div>
);

/** Table section refresh — toolbar + table shell like TenantsPageSkeleton. */
export const DataTableWithToolbarSectionSkeleton: React.FC<{ rows?: number; columns?: number }> = ({
  rows = 6,
  columns = 4,
}) => (
  <div aria-busy="true" aria-label="Loading table">
    <PortfolioToolbar>
      <TabPillRow>
        <Pill />
        <Pill />
        <Pill />
        <Pill />
      </TabPillRow>
      <Block $h={38} style={{ flex: '1 1 220px', minWidth: 180, maxWidth: 360 }} $mb={0} />
      <Block $h={38} $w="96px" $mb={0} />
    </PortfolioToolbar>
    <TableSectionBody rows={rows} columns={columns} />
  </div>
);

const MessagesTemplatesHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 20px;
`;

const MessagesTemplatesHeaderTop = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  @media (min-width: 640px) {
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
  }
`;

const MessagesFiltersRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  @media (min-width: 640px) {
    flex-direction: row;
    align-items: center;
  }
`;

const TemplatesGridSkeleton = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const HistoryHeaderSkeleton = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 20px;
`;

const HistoryHeaderTopSk = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  @media (min-width: 640px) {
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
  }
`;

const HistoryActionsSk = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  @media (min-width: 640px) {
    flex-direction: row;
    align-items: center;
    width: auto;
  }
`;

const HistoryCardRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;
`;

const ReminderCardSk = styled.div`
  border-radius: 12px;
  border: 1px solid ${(p) => skelEdge(p)};
  background: ${(p) => skelSurface(p)};
  padding: 18px;
  animation: ${pulse} 1.15s ease-in-out infinite;
`;

const HistoryAvatarSk = styled.div`
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: ${(p) => skelSurface(p)};
  border: 1px solid ${(p) => skelEdge(p)};
`;

const HistoryCardBody = styled.div`
  flex: 1;
  min-width: 0;
`;

/** Non-pulsing line inside a pulsing card shell (avoids stacked animations). */
const SkLine = styled.div<{ $h: number; $w?: string; $mb?: number }>`
  height: ${(p) => p.$h}px;
  width: ${(p) => p.$w ?? '100%'};
  border-radius: 6px;
  background: ${(p) => skelSurface(p)};
  border: 1px solid ${(p) => skelEdge(p)};
  margin-bottom: ${(p) => p.$mb ?? 0}px;
  opacity: 0.92;
`;

/** Properties: page title + stats + portfolio card (toolbar + property grid). */
export const PropertiesPageSkeleton: React.FC = () => (
  <Wrap aria-busy="true" aria-label="Loading properties">
    <Block $h={28} $w="42%" $mb={8} />
    <Block $h={16} $w="58%" $mb={22} />
    <StatsRow>
      <CardBlock $h={96} $mb={0} />
      <CardBlock $h={96} $mb={0} />
      <CardBlock $h={96} $mb={0} />
      <CardBlock $h={96} $mb={0} />
    </StatsRow>
    <PortfolioCard>
      <PortfolioHeaderBar>
        <Block $h={18} $w="38%" $mb={0} />
        <Block $h={32} $w="32px" $mb={0} style={{ borderRadius: 8 }} />
      </PortfolioHeaderBar>
      <PortfolioToolbar>
        <TabPillRow>
          <Pill />
          <Pill />
          <Pill />
          <Pill />
        </TabPillRow>
        <Block $h={38} style={{ flex: '1 1 220px', minWidth: 180, maxWidth: 360 }} $mb={0} />
      </PortfolioToolbar>
      <PropertyPortfolioGridSkeleton />
    </PortfolioCard>
  </Wrap>
);

/** Property units page: hero card + stats + units table shell. */
export const PropertyUnitsPageSkeleton: React.FC = () => (
  <Wrap aria-busy="true" aria-label="Loading property units">
    <PortfolioCard>
      <PortfolioHeaderBar>
        <Block $h={32} $w="132px" $mb={0} />
        <div style={{ display: 'flex', gap: 8 }}>
          <Block $h={32} $w="110px" $mb={0} />
          <Block $h={32} $w="120px" $mb={0} />
        </div>
      </PortfolioHeaderBar>
      <div style={{ padding: 16 }}>
        <Block $h={22} $w="42%" $mb={8} />
        <Block $h={14} $w="58%" $mb={14} />
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Pill />
          <Pill />
          <Pill />
        </div>
      </div>
    </PortfolioCard>
    <StatsRow>
      <CardBlock $h={96} $mb={0} />
      <CardBlock $h={96} $mb={0} />
      <CardBlock $h={96} $mb={0} />
      <CardBlock $h={96} $mb={0} />
    </StatsRow>
    <PortfolioCard>
      <PortfolioHeaderBar>
        <Block $h={20} $w="180px" $mb={0} />
        <Block $h={32} $w="32px" $mb={0} />
      </PortfolioHeaderBar>
      <PortfolioToolbar>
        <TabPillRow>
          <Pill />
          <Pill />
          <Pill />
        </TabPillRow>
        <Block $h={38} style={{ flex: '1 1 220px', minWidth: 180, maxWidth: 360 }} $mb={0} />
      </PortfolioToolbar>
      <div style={{ padding: 16 }}>
        <TableShell>
          <TableHead>
            <Block $h={14} $mb={0} />
            <Block $h={14} $mb={0} />
            <Block $h={14} $mb={0} />
            <Block $h={14} $mb={0} />
          </TableHead>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <TableRow key={i}>
              <Block $h={16} $mb={0} />
              <Block $h={16} $mb={0} />
              <Block $h={16} $mb={0} />
              <Block $h={16} $mb={0} />
            </TableRow>
          ))}
        </TableShell>
      </div>
    </PortfolioCard>
  </Wrap>
);

/** Tenant management page: title + stats + tenant table shell. */
export const TenantsPageSkeleton: React.FC = () => (
  <Wrap aria-busy="true" aria-label="Loading tenants">
    <Block $h={28} $w="42%" $mb={8} />
    <Block $h={16} $w="58%" $mb={22} />
    <StatsRow>
      <CardBlock $h={96} $mb={0} />
      <CardBlock $h={96} $mb={0} />
      <CardBlock $h={96} $mb={0} />
      <CardBlock $h={96} $mb={0} />
    </StatsRow>
    <PortfolioCard>
      <PortfolioHeaderBar>
        <Block $h={20} $w="150px" $mb={0} />
        <Block $h={32} $w="32px" $mb={0} />
      </PortfolioHeaderBar>
      <PortfolioToolbar>
        <TabPillRow>
          <Pill />
          <Pill />
          <Pill />
          <Pill />
        </TabPillRow>
        <Block $h={38} style={{ flex: '1 1 220px', minWidth: 180, maxWidth: 360 }} $mb={0} />
      </PortfolioToolbar>
      <div style={{ padding: 16 }}>
        <TableShell>
          <TableHead>
            <Block $h={14} $mb={0} />
            <Block $h={14} $mb={0} />
            <Block $h={14} $mb={0} />
            <Block $h={14} $mb={0} />
          </TableHead>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <TableRow key={i}>
              <Block $h={16} $mb={0} />
              <Block $h={16} $mb={0} />
              <Block $h={16} $mb={0} />
              <Block $h={16} $mb={0} />
            </TableRow>
          ))}
        </TableShell>
      </div>
    </PortfolioCard>
  </Wrap>
);

/** Message templates tab: matches MessageTemplates header + 1/2/3 column grid. */
export const MessagesTemplatesSkeleton: React.FC = () => (
  <Wrap aria-busy="true" aria-label="Loading templates" style={{ minHeight: 320 }}>
    <Block $h={28} $w="42%" $mb={8} />
    <Block $h={16} $w="58%" $mb={22} />
    <StatsRow>
      <CardBlock $h={96} $mb={0} />
      <CardBlock $h={96} $mb={0} />
      <CardBlock $h={96} $mb={0} />
      <CardBlock $h={96} $mb={0} />
    </StatsRow>
    <PortfolioCard>
      <PortfolioHeaderBar>
        <Block $h={18} $w="34%" $mb={0} />
        <Block $h={32} $w="32px" $mb={0} style={{ borderRadius: 8 }} />
      </PortfolioHeaderBar>
      <PortfolioToolbar>
        <TabPillRow>
          <Pill />
          <Pill />
        </TabPillRow>
        <Block $h={38} style={{ flex: '1 1 240px', minWidth: 200 }} $mb={0} />
        <Block $h={38} $w="min(100%, 160px)" $mb={0} />
      </PortfolioToolbar>
      <div style={{ padding: '16px' }}>
        <TemplatesGridSkeleton>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <CardBlock key={i} $h={152} $mb={0} />
          ))}
        </TemplatesGridSkeleton>
      </div>
    </PortfolioCard>
  </Wrap>
);

/** Message history tab: matches HistoryHeader + ReminderCard-style rows. */
export const MessagesHistorySkeleton: React.FC = () => (
  <Wrap aria-busy="true" aria-label="Loading communication history" style={{ minHeight: 320 }}>
    <Block $h={28} $w="42%" $mb={8} />
    <Block $h={16} $w="58%" $mb={22} />
    <StatsRow>
      <CardBlock $h={96} $mb={0} />
      <CardBlock $h={96} $mb={0} />
      <CardBlock $h={96} $mb={0} />
      <CardBlock $h={96} $mb={0} />
    </StatsRow>
    <PortfolioCard>
      <PortfolioHeaderBar>
        <Block $h={18} $w="34%" $mb={0} />
        <Block $h={32} $w="32px" $mb={0} style={{ borderRadius: 8 }} />
      </PortfolioHeaderBar>
      <PortfolioToolbar>
        <Block $h={38} style={{ flex: '1 1 240px', minWidth: 200 }} $mb={0} />
        <Block $h={38} $w="min(100%, 180px)" $mb={0} />
        <Block $h={38} $w="min(100%, 160px)" $mb={0} />
      </PortfolioToolbar>
      <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        {[1, 2, 3, 4, 5].map((i) => (
          <ReminderCardSk key={i}>
            <HistoryCardRow>
              <HistoryAvatarSk />
              <HistoryCardBody>
                <SkLine $h={16} $w="55%" $mb={10} />
                <SkLine $h={12} $w="88%" $mb={8} />
                <SkLine $h={12} $w="72%" $mb={0} />
              </HistoryCardBody>
            </HistoryCardRow>
          </ReminderCardSk>
        ))}
      </div>
    </PortfolioCard>
  </Wrap>
);

/** @deprecated Prefer MessagesTemplatesSkeleton or MessagesHistorySkeleton for accurate layout. */
export const MessagesTabSkeleton: React.FC = () => <MessagesTemplatesSkeleton />;

/** Settings panel main area while switching sections. */
export const SettingsSectionSkeleton: React.FC = () => (
  <div aria-busy="true" aria-label="Loading section" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
    <Block $h={22} $w="45%" $mb={0} />
    <Block $h={14} $w="72%" $mb={8} />
    <Block $h={120} $mb={0} />
    <Block $h={48} $mb={0} />
    <Block $h={48} $mb={0} />
    <Block $h={200} $mb={0} />
  </div>
);

/** Compact blocks for settings subsections (replaces “Loading…” text). */
export const SettingsInlineSkeleton: React.FC = () => (
  <div aria-busy="true" aria-label="Loading" style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '4px 0' }}>
    <Block $h={18} $w="52%" $mb={0} />
    <Block $h={14} $w="88%" $mb={0} />
    <Block $h={14} $w="70%" $mb={0} />
    <Block $h={44} $mb={0} />
  </div>
);

/** Payment channels grid placeholder. */
export const SettingsChannelGridSkeleton: React.FC = () => (
  <div
    aria-busy="true"
    aria-label="Loading channels"
    style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}
  >
    <CardBlock $h={132} $mb={0} />
    <CardBlock $h={132} $mb={0} />
  </div>
);

/** Users table body while fetching team users. */
export const UsersTableLoadingSkeleton: React.FC = () => (
  <div aria-busy="true" aria-label="Loading users" style={{ padding: '20px 16px' }}>
    {[1, 2, 3, 4, 5].map((i) => (
      <div
        key={i}
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(6, minmax(0, 1fr))',
          gap: 12,
          marginBottom: 14,
          alignItems: 'center',
        }}
      >
        <Block $h={14} $mb={0} />
        <Block $h={14} $mb={0} />
        <Block $h={14} $mb={0} />
        <Block $h={14} $mb={0} />
        <Block $h={14} $mb={0} />
        <Block $h={14} $mb={0} />
      </div>
    ))}
  </div>
);

const RolesGridSkeletonWrap = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  width: 100%;
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (min-width: 1280px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

const RoleCardSk = styled.div`
  border-radius: 12px;
  border: 1px solid ${(p) => skelEdge(p)};
  background: ${(p) => skelSurface(p)};
  padding: 16px;
  min-height: 148px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  animation: ${pulse} 1.15s ease-in-out infinite;
`;

const RoleCardFooterSk = styled.div`
  margin-top: auto;
  padding-top: 12px;
  border-top: 1px solid ${(p) => skelEdge(p)};
`;

/** Team settings → Roles tab while roles are loading. */
export const TeamRolesGridSkeleton: React.FC = () => (
  <RolesGridSkeletonWrap aria-busy="true" aria-label="Loading roles">
    {[1, 2, 3, 4].map((i) => (
      <RoleCardSk key={i}>
        <Block $h={18} $w="55%" $mb={0} />
        <Block $h={12} $w="36%" $mb={0} />
        <Block $h={14} $w="100%" $mb={0} />
        <Block $h={14} $w="88%" $mb={0} />
        <RoleCardFooterSk>
          <Block $h={12} $w="45%" $mb={0} />
        </RoleCardFooterSk>
      </RoleCardSk>
    ))}
  </RolesGridSkeletonWrap>
);

export default PageRouteSkeleton;
